import React, { ReactElement } from 'react';
import cn from 'classnames';
import { Button, Checkbox, Logo } from '@odin-labs/components';
import { kebabOrSnakeToSentenceCase } from 'utils';
import { AuthContext } from 'auth';
import { useRecordPolicyConsentMutation } from 'apollo/generated/client-operations';
import { PolicyConsentRow } from './PolicyConsentRow';
import { PolicyConsentStatusFragment, isPolicyAccepted } from './helpers';

const classes = {
  overlay: cn(
    // Unskippable, full-viewport, above all authenticated chrome.
    'odin-fixed odin-inset-0 odin-z-50 odin-overflow-y-auto',
    'odin-bg-gray-50 odin-flex odin-items-center odin-justify-center odin-p-4',
  ),
  card: cn(
    'odin-w-full odin-max-w-xl',
    'odin-bg-white odin-rounded-2xl odin-shadow odin-p-6 sm:odin-p-8',
    'odin-space-y-5',
  ),
  title: cn('odin-text-2xl odin-font-bold odin-text-gray-900'),
  subtitle: cn('odin-text-sm odin-text-gray-600'),
  list: cn(
    'odin-border odin-border-gray-200 odin-rounded-xl',
    'odin-divide-y odin-divide-gray-200',
  ),
  consent: cn('odin-flex odin-items-start odin-text-sm odin-text-gray-700'),
  error: cn('odin-text-sm odin-text-red-600'),
  logout: cn('odin-text-center'),
  logoutLink: cn('odin-underline'),
};

export type PolicyConsentGateProps = {
  /**
   * The full current policy set annotated with the user's consent history.
   * Accepted entries render for context; outstanding ones are what the checkbox
   * affirms.
   */
  policyConsentStatuses: PolicyConsentStatusFragment[];
  /**
   * Refetch to run after consent is recorded, re-reading the session that
   * decides gate visibility so it self-clears. Defaults to the AuthContext
   * session refetch (AppLayout). The self-onboarding flow drives the gate off
   * its own session query and passes that query's refetch instead.
   */
  onConsentRecorded?: () => void | Promise<unknown>;
};

/**
 * Global, unskippable re-capture gate. Rendered in front of all authenticated
 * routes when the current user has any outstanding policy consent (see
 * `AppLayout`). It lists every current policy — already-accepted ones for
 * context — and a single aggregate checkbox affirming the outstanding ones. On
 * accept, one consent row is written per outstanding document via
 * `recordPolicyConsent` and the session is re-fetched, which clears the gate.
 * Logging out is the only other exit.
 */
export function PolicyConsentGate({
  policyConsentStatuses,
  onConsentRecorded,
}: PolicyConsentGateProps): ReactElement {
  const { fetchCurrentSession, authState } = React.useContext(AuthContext);
  const [agreed, setAgreed] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [recordConsent, { loading }] = useRecordPolicyConsentMutation();

  const outstanding = policyConsentStatuses.filter(
    (status) => !isPolicyAccepted(status),
  );

  const handleAgree = async (): Promise<void> => {
    setHasError(false);
    try {
      await recordConsent({
        variables: {
          input: {
            policyDocumentIds: outstanding.map((status) => status.document.id),
          },
        },
      });
      // Re-run the session query; once the consent rows exist the outstanding
      // set is empty and the gate stops rendering. Awaited so a rejected
      // refetch surfaces through the same catch instead of going unhandled.
      await (onConsentRecorded ?? fetchCurrentSession)();
    } catch {
      setHasError(true);
    }
  };

  return (
    <div className={classes.overlay}>
      <div
        className={classes.card}
        role="dialog"
        aria-modal="true"
        // A single gate renders at a time (it blocks the whole app), so a
        // constant id tying the dialog to its heading is safe.
        aria-labelledby="policy-consent-gate-title"
      >
        <Logo logoStyle="normal" size="md" />

        <div className="odin-space-y-2">
          <h1 id="policy-consent-gate-title" className={classes.title}>
            We&apos;ve updated our legal terms
          </h1>
          <p className={classes.subtitle}>
            Before you continue, please review and accept the current versions
            of the documents below.
          </p>
        </div>

        <div className={classes.list}>
          {policyConsentStatuses.map((status) => (
            <PolicyConsentRow key={status.document.id} status={status} />
          ))}
        </div>

        <label className={classes.consent}>
          <Checkbox
            checked={agreed}
            onChange={(e): void => setAgreed(e.target.checked)}
          />
          <span>
            I have read and agree to the{' '}
            {outstanding.map((status, index) => (
              <React.Fragment key={status.document.id}>
                {index > 0 &&
                  (index === outstanding.length - 1 ? ' and ' : ', ')}
                <strong>
                  {kebabOrSnakeToSentenceCase(status.document.key)}
                </strong>
              </React.Fragment>
            ))}
            .
          </span>
        </label>

        {hasError && (
          <p className={classes.error}>
            Something went wrong recording your acceptance. Please try again.
          </p>
        )}

        <Button
          size="lg"
          text="Agree and continue"
          onClick={handleAgree}
          disabled={!agreed || loading}
          withSpinner={loading}
          className="odin-w-full odin-justify-center"
        />

        <div className={classes.logout}>
          <Button
            theme="naked"
            text="Log out"
            className={classes.logoutLink}
            onClick={(): void => authState.signOut()}
          />
        </div>
      </div>
    </div>
  );
}
